import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@store/authStore'
import publicApiClient from '@services/api/publicClient'
import { storageURL, mediaUrl } from '@config'
import { encodeId, decodeId } from '@utils/idCipher'
import toast from 'react-hot-toast'

function BannerDetailPage() {
  const { id: encodedId } = useParams()
  const id = decodeId(encodedId) // id réel pour les appels API
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [banner, setBanner] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showShareModal, setShowShareModal] = useState(false)
  const incrementedIdRef = useRef(null)

  useEffect(() => {
    if (!id) return
    const fetchBanner = async () => {
      try {
        setLoading(true)
        const res = await publicApiClient.get(`/posts/${id}`)
        const raw = res.data || res

        setBanner({
          id:          raw.post_id,
          name:        raw.libele,
          description: raw.description,
          link:        raw.siteWeb,
          image:       raw.fichiers?.[0]?.chemin ? mediaUrl(raw.fichiers[0].chemin) : null,
          date:        raw.created_at,
          status:      raw.status,
          clientId:    raw.post_client_id,
        })
      } catch (err) {
        console.error('Erreur lors du chargement de la bannière:', err)
        setBanner(null)
      } finally {
        setLoading(false)
      }
    }
    fetchBanner()
  }, [id])

  // Incrément de vue — guard contre double-appel StrictMode
  useEffect(() => {
    if (!id || incrementedIdRef.current === id) return
    incrementedIdRef.current = id
    publicApiClient.post(`/posts/${id}/view`).catch(() => {})
  }, [id])

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37]"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    )
  }

  if (!banner) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="mt-2 text-lg font-medium text-gray-900">Bannière introuvable</h3>
          <p className="mt-1 text-sm text-gray-500">Cette bannière n'existe pas ou a été supprimée.</p>
          <div className="mt-6">
            <button
              onClick={() => navigate('/dashboard')}
              className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-[#D4AF37] hover:bg-[#C19B2E]"
            >
              Retour au tableau de bord
            </button>
          </div>
        </div>
      </div>
    )
  }

  const isOwner = user?.client_id === banner.clientId || user?.id === banner.clientId

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="w-full lg:w-[90%] xl:w-[85%] mx-auto p-4 md:p-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-[#D4AF37] transition-colors mb-4"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Retour
        </button>

        <div className="space-y-6">
          {/* Image de la bannière */}
          <div className="bg-white rounded-xl overflow-hidden">
            {banner.image ? (
              <img
                src={banner.image}
                alt={banner.name || 'Bannière'}
                className="w-full h-auto object-contain"
                onError={(e) => {
                  e.target.onerror = null
                  e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="300"%3E%3Crect width="800" height="300" fill="%23f3f4f6"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="18" fill="%239ca3af"%3EImage non disponible%3C/text%3E%3C/svg%3E'
                }}
              />
            ) : (
              <div className="w-full h-64 bg-gray-100 flex items-center justify-center text-gray-400">
                Image non disponible
              </div>
            )}
          </div>

          {/* Informations */}
          <div className="bg-white rounded-xl p-4 md:p-6">
            <h1 className="text-xl md:text-2xl font-bold text-[#001F3F] mb-3">{banner.name}</h1>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-4 text-sm text-gray-600">
                {banner.date && (
                  <div className="flex items-center gap-1">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span>{new Date(banner.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowShareModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                  <span className="text-sm font-medium hidden sm:inline">Partager</span>
                </button>

                {banner.link && (
                  <a
                    href={banner.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-white rounded-full hover:bg-[#C19B2E] transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    <span className="text-sm font-medium hidden sm:inline">Visiter le site</span>
                  </a>
                )}

                {isOwner && (
                  <button
                    onClick={() => navigate(`/edit-banner/${banner.id}`)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <span className="text-sm font-medium hidden sm:inline">Modifier</span>
                  </button>
                )}
              </div>
            </div>

            {banner.description && (
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-[#001F3F] mb-2">Description</h2>
                <p className="text-gray-700 leading-relaxed">{banner.description}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal partage */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-[#001F3F]">Partager cette bannière</h3>
              <button
                onClick={() => setShowShareModal(false)}
                className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={`${window.location.origin}/banner/${encodeId(banner.id)}`}
                readOnly
                className="flex-1 px-4 py-2 bg-gray-100 rounded-lg text-sm"
              />
              <button
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/banner/${encodeId(banner.id)}`)
                  toast.success('Lien copié !')
                  setShowShareModal(false)
                }}
                className="px-4 py-2 bg-[#D4AF37] text-white rounded-lg hover:bg-[#c09d2a] transition-colors"
              >
                Copier
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default BannerDetailPage
